import { webApiClient } from '../authentication';
import { ShippingAddress } from './address';

/**
 * Recurly asked for a 3D Secure challenge.
 *
 * nano cannot complete one: the challenge is rendered by `ThreeDSecureAction` from
 * `@recurly/react-recurly`, and nano deliberately has no recurly.js. Callers hand these shoppers to
 * the shop, which can finish the purchase. Rare here in practice -- 3DS is mostly an EU/SCA
 * requirement and this is a US-only $20 charge -- but not impossible, so it is handled rather than
 * swallowed.
 */
export class ThreeDSecureUnsupportedError extends Error {
  constructor() {
    super('This card needs an extra verification step we cannot show here.');
    this.name = 'ThreeDSecureUnsupportedError';
  }
}

/** An error with a message safe to show the shopper, as returned by the endpoint. */
export class CheckoutCustomerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CheckoutCustomerError';
  }
}

interface StripeCheckoutResponse {
  userId: string;
}

interface EndpointErrorBody {
  error?: { code?: string; message?: string; token?: string };
}

function errorBodyOf(error: unknown): EndpointErrorBody | undefined {
  const response = (error as { response?: { status?: number; data?: EndpointErrorBody } }).response;
  return response?.data;
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } }).response?.status;
}

/**
 * Stores the card as billing info and saves the shipping address, in one call.
 *
 * With a null `confirmationToken` only the shipping address is saved: the endpoint treats the token as
 * optional, and the purchase then charges the billing info already on the Recurly account. That is the
 * card-on-file path.
 *
 * The shopper is always already authenticated here -- they just registered a chip -- so the endpoint
 * takes its `if (user)` branch and skips the 409 account-exists gate entirely. That is why nano needs
 * none of ecommerce's new-vs-existing-user branching or its inline-login retry.
 *
 * `tenancy` is deliberately not sent: nano has no tenancy concept, and the endpoint only validates the
 * field when it is present.
 */
export async function stripeCheckout(confirmationToken: string | null, email: string, address: ShippingAddress) {
  try {
    const { data } = await webApiClient.post<StripeCheckoutResponse>('/api/ecommerce/stripe-checkout', {
      confirmationToken: confirmationToken ?? undefined,
      email,
      firstName: address.firstName,
      lastName: address.lastName,
      address1: address.line1,
      address2: address.line2 || undefined,
      city: address.city,
      state: address.state,
      phone: address.phone,
      zip: address.zip,
      country: address.country,
    });
    return data;
  } catch (error) {
    const body = errorBodyOf(error);

    // a 3DS challenge arrives as a 400 customer_error carrying an action token
    if (statusOf(error) === 400 && body?.error?.code === 'customer_error' && body.error.token) {
      throw new ThreeDSecureUnsupportedError();
    }
    if (body?.error?.message) {
      throw new CheckoutCustomerError(body.error.message);
    }
    throw error;
  }
}
